import BG1 from './bg1.js';
import BG2 from './bg2.js';

/**
 * LpBg
 * 背景のcanvas。描画そのものは canvas 担当から受け取った bg1.js / bg2.js
 * （`window.BG1` / `window.BG2`）が持ち、ここは LP の初期化の流儀
 * （`js-lp-*` を app 側が拾う）に合わせるための薄いラップ。
 * どちらを使うかは `data-lp-bg` で指定する（`bg1` ＝KV上部／`bg2` ＝08〜10の下部バンド）。
 *
 * bg1.js / bg2.js は原本（canvas担当の管理）を無改変で複製したもので、
 * **末尾の `export default window.BGn;` の1行だけが差分**。原本を更新するときは
 * 複製し直して同じ1行を足す。**整形は当てない**（差分が増えて追従できなくなる）。
 * プロジェクトの整形規約に合わないので、gulpfile の taskEslint で除外している。
 *
 * 渡す値は canvas 担当と決めた確定値。理由なく変えない。
 *   type        … 'all'（動き全部入り）。**既定は 'none'（静止）なので明示が要る**
 *   objectScale … 0.5 固定。素材が2倍書き出しのため。**画面幅で絵の大きさを変えない**
 *   renderScale … 0.5。**実ピクセルを半分で描いてCSSで拡大する。**絵がぼけた光なので
 *                 粗さが見えない（元画像との平均絶対差 bg1 10.01→10.00／bg2 1.59→1.70。
 *                 最大誤差はむしろ125→109に下がる）。しかも15〜20%の不透明度で敷くので
 *                 差は実質1/5。面積が4枚合計15.4MP→3.8MPになり、ラスタライズがそのぶん軽い。
 *                 **`objectScale` とは別物**。あちらは絵の大きさ、こちらは描く解像度。
 *                 ※これ以降「実ピクセルの横幅＝CSS幅」では判定できない（CSS幅×0.5になる）
 *   bakeAhead   … 1。**画面外のリングの焼き上げを、1画面ぶん手前まで後回しにする。**
 *                 既定は読み込み時に4枚とも焼く作りで、そのぶんが読み込みを重くしていた
 *                 （実測：canvas1枚あたり Script Evaluation 83ms・4枚で323ms）。
 *                 効くのはbg2の2枚だけ。bg1は create() の中でまとめて焼き切る作りで
 *                 途中で止められず、`onReady` も無いので対象外（取り分も17ms程度）。
 *                 下敷きが最初から出ているので、遅れて焼き上がっても見え方は変わらない。
 *   bakeBudget  … 3。**`bakeAhead` を使うなら一緒に下げないと引っ掛かる。**
 *                 既定6msのままだと、焼き始める区間だけコマ中央値が18.2→24.2msに落ちる
 *                 （3回とも同値。55fps相当→41fps相当）。3msにすると18.2msに戻る。
 *                 コマ数は倍になるが、1画面ぶんの先読みがあるので手前で焼き上がる。
 *   turn        … bg1を左右反転（`data-turn="true"`）
 *   rotate      … bg2を180度回転（`data-rotate="true"`）
 *   container   … **渡さない**。既定＝canvas 自身
 *   manual      … 渡さない（自走）。バンド全面に敷く置き方ではスクロール追従が要らないため。
 *                 ビューポート幅の窓として敷く方式に変える場合だけ manual: true にして、
 *                 LpSmoothScroll の rAF で lenis.raf() の直後に bg.tick() を呼ぶ。
 *                 **その場合 bg1 は焼き上げが create の外へ出て、画面外だと進まなくなる**
 *                 （bg2は画面外でも進む）。変えるときは canvas 担当に先に相談すること。
 *
 * 上限フレームレート（30）と位置追従の間引き（trackFps）は**こちらで触らない**。
 * モジュールの中で「動きの進行は30fps上限／位置合わせは毎フレーム」に分かれていて、
 * 呼び出し側で間引くと位置追従まで鈍ってスクロールがガクつく。
 *
 * 自動起動の目印（bg1＝`data-type` ／ bg2＝`data-bg2`）は**付けない**。
 * ここから create() を明示的に呼ぶので、付けると二重に起きる。
 *
 * **向きの指定（`data-turn` / `data-rotate`）は、ここで読んで create() に渡す。**
 * エンジンは属性からも読めるが、それは自動起動の経路の話で、明示的に create() を
 * 呼ぶこちらでは効かない。**属性はマークアップに向きを書いておくためだけのもの**で、
 * 実際に効かせているのは下の create() の引数。
 *
 * 同じ絵がページの別の場所にもう一度、向きを変えて敷かれている（デザインの実測）。
 *   bg1 … 23〜27（`10339:11650`）で**左右反転**
 *   bg2 … 20〜22（`10339:11593`）で**180度回転**
 * どちらもFigmaの塗りの変換に入っていて、原画にもCSSにも出てこない（詳細は 35_lp.md）。
 *
 * 画面外・タブ非表示での停止、リサイズ時の焼き直し、prefers-reduced-motion は
 * bg1.js / bg2.js が持つ。
 *
 * 背景は「下敷きの画像 → canvas」の順で出す。出る順番は3段。
 *   ① 下敷き（`.p-lp-bg::before`）がCSSアニメーションでフェードイン（JS不要）
 *   ② それが終わり、**かつ**canvasが描き上がるまで待つ
 *   ③ canvasに `.is-ready` を付けてクロスフェード
 * ②の待ち合わせがここの仕事。片方だけ先に済んでも進めない。
 *
 * **焼き上げ（リングの作り置き）が済むまで絵は欠けている。**
 * 済んだ合図の受け取り方がエンジンで違うので、両方に対応している。
 *   bg2 … `onReady()` を持つ。焼き上げを毎フレーム 6ms ずつに分けて進める作りなので、
 *          合図が来てからフェードを始めないと、途中の欠けた絵が表に出る。
 *   bg1 … `onReady()` が無く、create() の中で焼き切る。戻った時点で完了している
 *          （実寸 1440×3459 でも 5〜7ms と軽い）ので、そのままフェードへ進む。
 *
 * **どちらも読み込み時に焼く。**bg2を画面到達まで後回しにする案を試したが、
 * 焼き上げがスクロール中に来てページが引っかかった（実測 257ms＋197ms）。
 */

const ENGINES = {
  bg1: BG1,
  bg2: BG2,
};

// フェードを始める前に、メインスレッドが落ち着くのを待つ条件。
// 読み込み中はページ自体のレイアウト/描画でフレームが飛ぶ。飛んでいる最中に
// フェードを始めると、その場で止まって解放された瞬間に一気に濃くなり
// 「パッと出た」ように見える。普通の間隔のフレームが続くようになってから始める。
const CALM_FRAMES = 3;
const CALM_FRAME_MS = 34;
// 遅い端末でいつまでも落ち着かない場合でも必ず出すための上限（フレーム数）
const CALM_MAX_FRAMES = 90;
// 下敷きのフェードイン（_p-lp-bg.scss の keyframes 名）。終了を待つために名前で拾う
const POSTER_ANIMATION = 'lp-bg-in';

export default class LpBg {
  /**
   * @param {HTMLCanvasElement} element
   */
  constructor(element) {
    this.element = element;
    this.engine = ENGINES[this.element && this.element.dataset.lpBg];
    if (!this.engine) {
      return;
    }

    this.create();
  }

  create() {
    const { dataset } = this.element;
    this.bg = this.engine.create(this.element, {
      type: 'all',
      objectScale: 0.5,
      renderScale: 0.5,
      bakeAhead: 1,
      bakeBudget: 3,
      turn: dataset.turn === 'true',
      rotate: dataset.rotate === 'true',
    });

    if (typeof this.bg.onReady === 'function') {
      this.bg.onReady(() => this.reveal());
      return;
    }
    this.reveal();
  }

  /**
   * 1コマ目が画面に出て、かつメインスレッドが落ち着いてからクロスフェードを始める。
   * 1コマ目は描き終わっているが、画面に反映されるのは次のフレーム。
   * さらに読み込み中はフレームが飛んでいて、その間に始めるとフェードが途中で止まり、
   * 解放された瞬間に一気に濃くなるため、普通の間隔のフレームが続くのを待つ。
   * そのうえで、**下敷きのフェードインが終わるまで**待ってから切り替える。
   */
  reveal() {
    let prev = 0;
    let calm = 0;
    let frames = 0;
    const tick = (now) => {
      frames += 1;
      calm = (prev && now - prev < CALM_FRAME_MS) ? calm + 1 : 0;
      prev = now;
      if (calm >= CALM_FRAMES || frames >= CALM_MAX_FRAMES) {
        this.afterPoster(() => this.element.classList.add('is-ready'));
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  /**
   * 下敷き（包んでいる .p-lp-bg の ::before）のフェードインが終わるのを待つ。
   * アニメーションはCSS側で自走しているので、その Animation を拾って終了を待つ。
   * 取れない環境（getAnimations 未対応・アニメーション無し）は待たずに進める。
   */
  afterPoster(done) {
    const box = this.element.parentElement;
    if (!box || typeof box.getAnimations !== 'function') {
      done();
      return;
    }
    const running = box.getAnimations({ subtree: true })
      .filter((a) => a.animationName === POSTER_ANIMATION);
    if (!running.length) {
      done();
      return;
    }
    Promise.all(running.map((a) => a.finished.catch(() => {}))).then(done);
  }

  destroy() {
    if (this.bg) {
      this.bg.destroy();
      this.bg = null;
    }
  }
}
